import React, { forwardRef, useImperativeHandle } from "react";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { useQuery, useQueryClient } from "react-query";
import moment from "moment";
import SelectedAssetMetricsReadDto from "../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import InsightsAnomaliesView from "../Components/Insights/InsightsAnomaliesView";
import type { InsightTableInput } from "../Components/Insights/types";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import anomalyApiService from "../Utilities/AnomalyApiService";

interface Props {
  asset: SelectedAssetMetricsReadDto;
  dateFilter: { startDate: Date; endDate: Date };
  setDateFilter: (filter: { startDate: Date; endDate: Date }) => void;
}

export interface AssetInsightsTabRef {
  refresh: () => Promise<void>;
}

const AssetInsightsTab = forwardRef<AssetInsightsTabRef, Props>(
  ({ asset, dateFilter, setDateFilter }, ref) => {
    const queryClient = useQueryClient();

    const handleStartDateChange = (event: any) => {
      if (event.value) {
        setDateFilter({
          ...dateFilter,
          startDate: moment(event.value).startOf("day").toDate(),
          endDate: dateFilter.endDate,
        });
      }
    };

    const handleEndDateChange = (event: any) => {
      if (event.value) {
        setDateFilter({
          ...dateFilter,
          endDate: moment(event.value).endOf("day").toDate(),
          startDate: dateFilter.startDate,
        });
      }
    };

    const insightsQuery = useQuery<InsightTableInput[]>(
      ["assetInsightsAnomalies", asset.id, dateFilter.startDate, dateFilter.endDate],
      async () => {
        const [anomalies, anchors] = await Promise.all([
          anomalyApiService.getAssetAnomaliesPayload(asset.id),
          anomalyApiService.getAssetAnchorsPayload(asset.id),
        ]);

        const rangeStart = moment(dateFilter.startDate);
        const rangeEnd = moment(dateFilter.endDate);
        const isWithinRange = (startDate: string, endDate: string | null) => {
          const itemStart = moment(startDate);
          // Ongoing anomalies have no end date: treat the end as "now" (open-ended).
          const itemEnd = endDate ? moment(endDate) : moment();

          if (!itemStart.isValid() || !itemEnd.isValid()) {
            return false;
          }

          return itemEnd.isSameOrAfter(rangeStart) && itemStart.isSameOrBefore(rangeEnd);
        };

        return [
          ...anomalies
            .filter((anomaly) => isWithinRange(anomaly.startDate, anomaly.endDate))
            .map((anomaly) => ({ kind: "anomaly" as const, data: anomaly })),
          ...anchors
            .filter((anchor) => isWithinRange(anchor.startDate, anchor.endDate))
            .map((anchor) => ({ kind: "anchor" as const, data: anchor })),
        ];
      },
      {
        enabled: !!asset.id,
      }
    );

    useImperativeHandle(ref, () => ({
      refresh: async () => {
        await queryClient.invalidateQueries(["assetInsightsAnomalies", asset.id]);
      },
    }), [asset.id, queryClient]);

    return (
      <div className="flex-1 min-h-0 flex flex-col p-4 m-2 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="mb-2 flex flex-wrap items-center justify-end gap-3">
          <div className="flex items-center gap-x-2">
            <DatePicker
              value={dateFilter.startDate}
              onChange={handleStartDateChange}
              max={dateFilter.endDate || undefined}
              width="150px"
            />
            <span>-</span>
            <DatePicker
              value={dateFilter.endDate}
              onChange={handleEndDateChange}
              min={dateFilter.startDate || undefined}
              max={moment().toDate()}
              width="150px"
            />
          </div>
        </div>
        <div className="flex-1 min-h-0" style={{ minHeight: 200 }}>
          {insightsQuery.isLoading ? (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <LoadingSpinner />
            </div>
          ) : (
            <InsightsAnomaliesView asset={asset} data={insightsQuery.data ?? []} />
          )}
        </div>
      </div>
    );
  }
);

AssetInsightsTab.displayName = "AssetInsightsTab";

export default AssetInsightsTab;
